import { useRef, useEffect, useState } from "react";
import { Volume2, VolumeX, ArrowRight, Play, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import demoVideo from "@/assets/thinkr-demo-video.mp4";

const VideoSection = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.muted = false;
            setIsMuted(false);
            video.play().then(() => {
              setIsPlaying(true);
            }).catch(() => {
              // Fallback to muted if autoplay with sound blocked
              video.muted = true;
              setIsMuted(true);
              video.play().then(() => {
                setIsPlaying(true);
              }).catch(() => {});
            });
          } else {
            video.muted = true;
            setIsMuted(true);
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(video);

    return () => observer.disconnect();
  }, []);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  return (
    <section className="w-full py-16 md:py-24 bg-background">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-normal text-gray-900">
            The future of ecommerce is <em>here.</em>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600">
            Get an unfair advantage. Grow your store.
          </p>
        </div>
        <div className="relative">
          <video
            ref={videoRef}
            src={demoVideo}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="w-full h-auto rounded-lg"
          />
          <div className="absolute bottom-4 right-4 flex items-center gap-2">
            <button
              onClick={togglePlay}
              className="bg-black/60 hover:bg-black/80 text-white p-3 rounded-lg transition-colors"
              aria-label={isPlaying ? "Pause video" : "Play video"}
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            </button>
            <button
              onClick={toggleMute}
              className="bg-black/60 hover:bg-black/80 text-white p-3 rounded-lg transition-colors"
              aria-label={isMuted ? "Unmute video" : "Mute video"}
            >
              {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
          </div>
        </div>
        
        <div className="mt-10 flex justify-center">
          <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 rounded-lg">
            <Link to="/contact">
              Book an intro
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
